import { Input, StarsButton, StarsDropDown } from '@ps/web-shared-components';
import type { SelectOption } from '@ps/web-shared-components';
import { createForm } from '@ps/web-shared-ui';
import { minLength, minNumber, required } from '@ps/web-shared-validators';
import type { FormApi } from 'final-form';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { lastValueFrom } from 'rxjs';

import useRequest from '@swf/core/hooks/useRequest';
import { select } from '@swf/core/store';
import { Welcome } from '@swf/help';

// TODO refactor to use composition or shared.scss
// eslint-disable-next-line no-restricted-imports
import styles from '../../styles/csf.scss';
import type { Category, CsfData } from '../../types/category';
import { UserShowField } from '../../types/enums';
import { generateCategoryData, generateLanguageList } from '../../utils';
import ErrorMessage from '../ErrorMessage/ErrorMessage';
import Faq from '../Faq/Faq';
import SuccessMessage from '../SuccessMessage/SuccessMessage';

import {
  TXTCLI_Comment,
  TXTCLI_Email,
  TXTCLI_Language,
  TXTCLI_Must_be_at_least_N0_characters_ELL,
  TXTCLI_Send,
  TXTCLI_Subject,
  TXTWCL_Message,
  TXTWCL_Reason_XWhat_is_your_query_abo_ELL
} from './ContactSupportForm.messages.json';

interface FormValues {
  categoryId: number;
  subcategoryId: number;
  subject: string;
  body: string;
  language: string;
  email?: string;
}

const Form = createForm<FormValues>();

const ContactSupportForm: React.FC = () => {
  const [{ alert, categories }, setCsfData] = useState<CsfData>({} as CsfData);
  const [categoriesData, setCategoriesData] = useState<SelectOption[]>([]);
  const [subCategoryData, setSubCategoryData] = useState<SelectOption[]>([]);
  const [curCategory, setCurCategory] = useState<Category>();
  const [curSubCategory, setCurSubCategory] = useState<Category>();
  const [success, setSuccess] = useState<boolean>();
  const [error, setError] = useState<boolean>();

  const locale = useSelector(select.locale.get);
  const siteLocales = useSelector(select.cms.getWebsiteLanguages);
  const userEmail = useSelector(select.account.getEmail);
  const { formatMessage } = useIntl();
  const request = useRequest();

  useEffect(() => {
    request<CsfData>({ service: '/api/v1-preview/csf/categories' }).subscribe({
      next: ({ body }) => {
        setCsfData(body);
        setCategoriesData(generateCategoryData(body.categories));
        setError(false);
      },
      error: () => setError(true)
    });
  }, [request]);

  const initialValues: FormValues = {
    categoryId: -1,
    subcategoryId: -1,
    subject: '',
    body: '',
    language: locale,
    email: userEmail
  };

  const checkDisplayFlag = useCallback(
    (positionIndex: number, defaultDisplay: boolean) => {
      const flag = curSubCategory?.flag || curCategory?.flag;
      // eslint-disable-next-line no-bitwise
      return flag ? !!(flag & positionIndex) : defaultDisplay;
    },
    [curCategory?.flag, curSubCategory?.flag]
  );

  const displaySubject = useMemo(
    () => checkDisplayFlag(UserShowField.displaySubject, true),
    [checkDisplayFlag]
  );

  const displayBody = useMemo(
    () => checkDisplayFlag(UserShowField.displayBody, true),
    [checkDisplayFlag]
  );

  const onSubmit = async (formValues: FormValues, { restart }: FormApi<FormValues>) => {
    await lastValueFrom(
      request({
        service: '/api/v1-preview/csf/submission',
        body: formValues,
        method: 'POST'
      })
    )
      .then(() => {
        setSuccess(true);
        restart();
      })
      .catch(() => setError(true));
  };

  const languagesDropdown = useCallback(() => generateLanguageList(siteLocales), [siteLocales]);

  const onSelectCategory = useCallback(
    ({ value }: SelectOption) => {
      const cur = categories.find((category) => category.id === value);
      if (!cur) {
        return;
      }
      setCurCategory(cur);

      const subCategory = cur?.subCategories as Category[];
      const data = subCategory?.map(({ name, id }) => ({
        text: name,
        value: id
      }));
      setSubCategoryData(data);
      setCurSubCategory(undefined);
    },
    [categories]
  );

  const onSelectSubCategory = useCallback(
    ({ value }: SelectOption) => {
      const cur = curCategory?.subCategories?.find((category) => category.id === value);
      if (cur) setCurSubCategory(cur);
    },
    [curCategory?.subCategories]
  );

  const help = useMemo(
    () => curSubCategory?.help || curCategory?.help || '',
    [curCategory, curSubCategory]
  );

  const getFaq = useMemo(
    () => (curSubCategory ? curSubCategory.faq : curCategory ? curCategory.faq : false),
    [curCategory, curSubCategory]
  );

  return (
    <Form
      onSubmit={onSubmit}
      initialValues={initialValues}
      render={({ hasValidationErrors }) => (
        <>
          <Welcome />
          {error && <ErrorMessage />}
          {success && <SuccessMessage />}
          {!!alert && <div dangerouslySetInnerHTML={{ __html: alert }} />}
          <Input
            id="email"
            label={TXTCLI_Email}
            name="email"
            value={userEmail}
            className={styles.hide}
          />
          <StarsDropDown
            id="language"
            label={TXTCLI_Language}
            value={locale}
            options={languagesDropdown()}
            hideLabel
            className={styles.hide}
          />
          <StarsDropDown
            id="categoryId"
            label={TXTWCL_Reason_XWhat_is_your_query_abo_ELL}
            options={categoriesData}
            onSelect={onSelectCategory}
            validators={[minNumber(0)]}
          />
          <br />
          {subCategoryData.length > 0 && (
            <StarsDropDown
              id="subcategoryId"
              label=""
              options={subCategoryData}
              onSelect={onSelectSubCategory}
              validators={[required()]}
            />
          )}
          {!!help && <div className={styles.info} dangerouslySetInnerHTML={{ __html: help }} />}
          {getFaq && <Faq category={curCategory} subCategory={curSubCategory} />}
          {(displaySubject || displayBody) && (
            <Input
              id="subject"
              label={TXTWCL_Message}
              name="subject"
              placeholder={formatMessage(TXTCLI_Subject)}
              validators={[
                minLength(2, { ...TXTCLI_Must_be_at_least_N0_characters_ELL, values: { 0: 2 } })
              ]}
            />
          )}
          <br />
          {displayBody && (
            <Input
              id="body"
              type="textarea"
              label={TXTCLI_Comment}
              hideLabel
              validators={[
                minLength(2, { ...TXTCLI_Must_be_at_least_N0_characters_ELL, values: { 0: 2 } })
              ]}
            />
          )}
          <br />
          <StarsButton
            block
            type="submit"
            translation={TXTCLI_Send}
            disabled={hasValidationErrors}
          />
        </>
      )}
    />
  );
};

export default ContactSupportForm;
